import posthog from "posthog-js";
import { useCallback, useMemo } from "react";

import { JobDetails } from "../utils/helpers";

/** Where the job detail view is rendered. */
export type RoleExplainerSurface = "job_details_page" | "job_drawer";

/**
 * What started a Role Explainer run.
 * `auto_reload` is the silent re-fetch for someone who already viewed this job.
 * `list_cta` is a deliberate open from a job list whose button promises an explanation,
 * so the run starts before the reader ever sees the in-drawer banner.
 */
export type RoleExplainerTrigger = "click" | "retry" | "auto_reload" | "list_cta";

/** Why a Role Explainer run produced no explanation. */
export type RoleExplainerFailureReason = "api_error" | "invalid_response";

interface JobAnalyticsData {
	jobId?: string;
	jobTitle?: string;
	companyName?: string;
	companyId?: string;
	surface?: RoleExplainerSurface;
}

/**
 * Hook for tracking job page analytics events
 * Events help answer: "What actually convinces people to apply?"
 */
export function useJobPageAnalytics(jobDetails: JobDetails, surface?: RoleExplainerSurface) {
	// Memoised so the trackers keep a stable identity - effects depend on them.
	const analyticsData: JobAnalyticsData = useMemo(
		() => ({
			jobId: jobDetails?._id,
			jobTitle: jobDetails?.jobTitle,
			companyName: jobDetails?.company?.company_name,
			companyId: jobDetails?.company?._id,
			surface,
		}),
		[jobDetails, surface],
	);

	// Role Explainer events
	const trackRoleExplainerOpened = useCallback(
		(trigger: Exclude<RoleExplainerTrigger, "auto_reload"> = "click") => {
			posthog.capture("role_explainer_opened", { ...analyticsData, trigger });
		},
		[analyticsData],
	);

	// Silent re-fetch for a returning viewer. Kept off role_explainer_opened so that
	// event stays a pure count of deliberate opens, comparable with its own history.
	const trackRoleExplainerAutoLoaded = useCallback(() => {
		posthog.capture("role_explainer_auto_loaded", analyticsData);
	}, [analyticsData]);

	// Fires when an explanation actually renders, not when the button is pressed.
	const trackRoleExplainerViewed = useCallback(
		(trigger: RoleExplainerTrigger) => {
			posthog.capture("role_explainer_viewed", { ...analyticsData, trigger });
		},
		[analyticsData],
	);

	const trackRoleExplainerFailed = useCallback(
		(reason: RoleExplainerFailureReason, trigger: RoleExplainerTrigger) => {
			posthog.capture("role_explainer_failed", { ...analyticsData, reason, trigger });
		},
		[analyticsData],
	);

	// FitCheck events
	const trackFitCheckStarted = useCallback(() => {
		posthog.capture("fitcheck_started", analyticsData);
	}, [analyticsData]);

	const trackFitCheckCompleted = useCallback(
		(matchLevel: "high" | "partial" | "low") => {
			posthog.capture("fitcheck_completed", {
				...analyticsData,
				matchLevel,
			});
		},
		[analyticsData],
	);

	// Apply events - with context about what preceded the apply
	const trackApplyClicked = useCallback(
		(context: { afterExplainer?: boolean; afterFitCheck?: boolean; directApply?: boolean }) => {
			const eventName = context.afterExplainer
				? "apply_clicked_after_explainer"
				: context.afterFitCheck
					? "apply_clicked_after_fitcheck"
					: "apply_clicked_direct";

			posthog.capture(eventName, {
				...analyticsData,
				...context,
			});
		},
		[analyticsData],
	);

	// Job interaction events
	const trackJobSaved = useCallback(() => {
		posthog.capture("job_saved", analyticsData);
	}, [analyticsData]);

	const trackJobShared = useCallback(
		(platform?: string) => {
			posthog.capture("job_shared", {
				...analyticsData,
				platform,
			});
		},
		[analyticsData],
	);

	const trackJobMarkedIrrelevant = useCallback(() => {
		posthog.capture("job_marked_irrelevant", analyticsData);
	}, [analyticsData]);

	// Job Description events
	const trackJobDescriptionExpanded = useCallback(() => {
		posthog.capture("job_description_expanded", analyticsData);
	}, [analyticsData]);

	// Job List events
	const trackExploreClicked = useCallback(() => {
		posthog.capture("job_list_explore_clicked", analyticsData);
	}, [analyticsData]);

	return {
		// Role Explainer
		trackRoleExplainerOpened,
		trackRoleExplainerAutoLoaded,
		trackRoleExplainerViewed,
		trackRoleExplainerFailed,
		// FitCheck
		trackFitCheckStarted,
		trackFitCheckCompleted,
		// Apply
		trackApplyClicked,
		// Interactions
		trackJobSaved,
		trackJobShared,
		trackJobMarkedIrrelevant,
		trackJobDescriptionExpanded,
		// Job List
		trackExploreClicked,
	};
}
